// ── AI-tjänst för PitchN ─────────────────────────────────────────────────────
import { callClaude } from '../../../utils/claudeProxy';

const MODEL = 'claude-opus-5';

const SYSTEM_PROMPT = `Du är en presentationsassistent för konsultföretaget Tikab Strukturmekanik AB.

KOMMUNIKATIONSGUIDE:
Ton: Direkt och substansdriven. Inga tomma buzzwords. Vi visar vad vi gjort, inte vad vi påstår oss vara.
Gör: "Vi byggde systemet för Rosenbad under säkerhetsskyddsklass." "Minst två medarbetare säkerhetsprövas inför varje uppdrag."
Gör inte: "Vi är ledande inom..." "Vi brinner för innovation."
Slide-regler: Max 5–6 ord per bullet. Undvik meningsbyggnad i listor. Preferera siffror och konkreta fakta.

IGENKÄNNINGSREGLER FÖR RÅDATA:
- Tabelldata med år/datum + siffror, få rader     → bar-chart (ej tillgängligt i MVP, använd stats)
- Procentandelar, fördelningar                    → stats
- Punktlista med ≤6 punkter                       → bullets
- 2–4 korta påståenden på varsin rad              → key-statements
- "Agenda", tidsangivelser, "min:"               → agenda (ej MVP, använd bullets)
- Numrerade steg, "sedan", "därefter"             → process
- Siffror att visa: grundat, medarbetare, etc.    → stats
- Affärsområdes-separator                          → divider
- Ett enskilt referensprojekt lyfts fram           → reference-single
- Flera referensprojekt (2–5) listas               → reference

BILDTILLDELNING (välj från backgroundImages-listan):
- cover       → bg-dark-cube-bright
- divider     → matcha affärsområde (mek→bg-dark-gear, filmremsa→bg-filmstrip, etc.) eller bg-dark-cube-dim
- stats       → bg-dark-office
- process     → bg-dark-mixed
- bullets     → bg-dark-cube-dim
- key-statements → bg-light-cube
- reference   → bg-dark-cube-dim
- closing     → bg-light-camera

Returnera ENBART ett JSON-objekt. Noll text utanför JSON.`;

function buildUserPrompt({ userPrompt, projects, backgroundIds }) {
  return `Skapa en presentation utifrån:
"${userPrompt}"

Tillgängliga slide-typer: hero, cover, divider, stats, process, bullets, key-statements, reference-single, reference, closing

Tillgängliga bakgrundsbilder: ${backgroundIds.join(', ')}

Tillgängliga referensprojekt från UppdragN:
${JSON.stringify(projects.map(p => ({
  id: p.id,
  projectNumber: p.projectNumber,
  name: p.name,
  description: p.description ? p.description.slice(0, 120) : '',
})), null, 2)}

Returnera:
{
  "title": "Presentationstitel",
  "suggestedClient": "Kundnamn om identifierbart",
  "slides": [
    // Varje slide MÅSTE ha "speakerNotes" (1–3 meningar) och "backgroundImage": {"id": "bg-id"}
    // Se slidetyper nedan:

    // hero: { type, title, tagline, client, backgroundImage, speakerNotes } — stor logotyp + rubrik + tagline
    // cover: { type, title, tagline, client, backgroundImage, speakerNotes }
    // divider: { type, title, backgroundImage, speakerNotes }
    // stats: { type, label, stats: [{value, unit, label}], backgroundImage, speakerNotes }
    // unit är valfritt — ex: "ST", "ÅR", "SEK", "%" — visas i orange bredvid siffran
    // process: { type, label, steps: [{number, title}], backgroundImage, speakerNotes }
    // bullets: { type, label, title, bullets: [string], backgroundImage, speakerNotes }
    // key-statements: { type, statements: [string], backgroundImage, speakerNotes }
    // reference-single: { type, project: {uppdragId, projectNumber, title, client, description, imageUrl}, backgroundImage, speakerNotes }
    // reference: { type, label, title, projects: [{uppdragId, projectNumber, title, description, imageUrl}], backgroundImage, speakerNotes }
    // closing: { type, label, callToAction, backgroundImage, speakerNotes }
  ]
}`;
}

export async function analyzeProjectForPitch({ project, pitchContext = {} }) {
  const system = `Du är en presentationsassistent för konsultföretaget Tikab Strukturmekanik AB.
Kondensera information om ett referensprojekt till presentationstext: faktadriven, konkret, inga tomma fraser.
Returnera ENBART ett JSON-objekt.`;

  const ctx = [
    pitchContext.title  && `Presentation: "${pitchContext.title}"`,
    pitchContext.client && `Kund: ${pitchContext.client}`,
    pitchContext.userPrompt && `Syfte/pitch: ${pitchContext.userPrompt}`,
  ].filter(Boolean).join('\n');

  const proj = [
    `Namn: ${project.title || ''}`,
    project.role        && `Vår roll: ${project.role}`,
    project.description && `Beskrivning: ${project.description}`,
    project.ourAssignment && `Vårt uppdrag: ${project.ourAssignment}`,
    project.keywords?.length && `Nyckelord: ${project.keywords.join(', ')}`,
    project.budget      && `Budget: ${project.budget}`,
  ].filter(Boolean).join('\n');

  const userMsg = `${ctx ? ctx + '\n\n' : ''}Referensprojekt:\n${proj}

Kondensera så att det passar i presentationen ovan. Max 2–3 meningar per fält.
Returnera:
{
  "description": "Kort projektbeskrivning (max 220 tecken)",
  "ourAssignment": "Kort uppdragsbeskrivning (max 320 tecken)"
}`;

  const data = await callClaude({
    model: MODEL, max_tokens: 512, system,
    messages: [{ role: 'user', content: userMsg }],
  });
  const raw = data.content?.[0]?.text || '{}';
  const cleaned = raw.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
  return JSON.parse(cleaned);
}

export async function generatePitch({ userPrompt, projects, backgrounds }) {
  const backgroundIds = backgrounds.map(b => b.id);

  const data = await callClaude({
    model: MODEL,
    max_tokens: 4096,
    system: SYSTEM_PROMPT,
    messages: [{
      role: 'user',
      content: buildUserPrompt({ userPrompt, projects, backgroundIds }),
    }],
  });
  const raw = data.content?.[0]?.text || '';

  // Rensa bort eventuell markdown-wrapper
  const cleaned = raw.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
  const parsed  = JSON.parse(cleaned);
  return parsed; // { title, suggestedClient, slides }
}
